import { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";

import { AppContexts } from "../../../providers";
import usePath from "../Common/History";

const MessageAudit = () => {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [guest, setGuest] = useState("");
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const {
    active,
    auditReservations,
    messageAudit,
    searchAuditReservations,
    getMessageAudit,
    setAuditReservations,
    setMessageAudit,
  } = useContext(AppContexts.ReportsContext);

  const path = usePath();

  useEffect(() => {
    if (path !== active) {
      setGuest("");
      setSelectedReservationId(null);
      setHasSearched(false);
      setSearching(false);
      setSearchError("");
      setAuditReservations([]);
      setMessageAudit([]);
    }
  }, [path, active, setAuditReservations, setMessageAudit]);

  const handleDateChange = useCallback((evt) => {
    const nextDate = evt.target.value;
    setDate(nextDate);
    setGuest("");
    setSelectedReservationId(null);
    setHasSearched(false);
    setSearching(false);
    setSearchError("");
    setAuditReservations([]);
    setMessageAudit([]);
  }, [setAuditReservations, setMessageAudit]);

  const handleGuestChange = useCallback((evt) => {
    const searchTerm = evt.target.value;
    setGuest(searchTerm);
    setSelectedReservationId(null);
    setHasSearched(false);
    setSearchError("");
    setMessageAudit([]);
    setAuditReservations([]);
  }, [setAuditReservations, setMessageAudit]);

  const handleGuestSearch = useCallback(async (evt) => {
    evt.preventDefault();
    if (active === "message-audit") {
      setSearching(true);
      setHasSearched(false);
      setSearchError("");
      try {
        await searchAuditReservations(date, guest.trim());
      } catch (err) {
        setSearchError("Reservation search failed. Please try again.");
      } finally {
        setHasSearched(true);
        setSearching(false);
      }
    }
  }, [active, date, guest, searchAuditReservations]);

  const handleReservationSelect = useCallback((reservation) => {
    setSelectedReservationId(String(reservation.guest_reservation_id));
    getMessageAudit(reservation.guest_reservation_id);
  }, [getMessageAudit]);

  const handleReservationChange = useCallback((evt) => {
    const guestReservationId = evt.target.value;
    const reservation = auditReservations.find(
      (item) => String(item.guest_reservation_id) === guestReservationId,
    );

    if (reservation) {
      handleReservationSelect(reservation);
    }
  }, [auditReservations, handleReservationSelect]);

  const formatDateTime = (value) => {
    if (!value) return "-";
    return moment.utc(value).local().format("MM/DD/YYYY hh:mm a");
  };

  return (
    <li className="reports-tab-content">
      <h2>Message Audit</h2>
      <p className="secondary">See why scheduled messages were sent or not sent.</p>

      <div>
        <div>
          <form className="uk-form-stacked" onSubmit={handleGuestSearch}>
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-4@m uk-width-1-1">
                <label className="uk-form-label">Check-In Date</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    type="date"
                    value={date}
                    onChange={handleDateChange}
                  />
                </div>
              </div>

              <div className="uk-width-1-3@m uk-width-1-1">
                <label className="uk-form-label">Guest Last Name</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    value={guest}
                    onChange={handleGuestChange}
                    placeholder="Search by guest name"
                  />
                </div>
              </div>

              <div className="uk-width-auto@m uk-width-1-1 uk-flex uk-flex-bottom">
                <button
                  className="uk-button hms-btn hms-red-btn"
                  type="submit"
                  disabled={!date || !guest.trim()}
                >
                  Search
                </button>
              </div>

              <div className="uk-width-1-3@m uk-width-1-1">
                <label className="uk-form-label">Matching Reservation</label>
                <div className="uk-form-controls">
                  <select
                    className="uk-select"
                    value={selectedReservationId || ""}
                    onChange={handleReservationChange}
                    disabled={!auditReservations.length}
                  >
                    <option value="">
                        {auditReservations.length
                          ? "Select a reservation"
                          : hasSearched
                            ? "No matching reservations found"
                            : "Search to load reservations"}
                    </option>
                    {auditReservations.map((reservation) => (
                      <option
                        key={reservation.guest_reservation_id}
                        value={reservation.guest_reservation_id}
                      >
                        {reservation.first_name} {reservation.last_name} - {reservation.pms_id} - {reservation.category || "-"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </form>
          {searching && (
            <div className="secondary uk-margin-small-top">Searching reservations...</div>
          )}
          {searchError && !searching && (
            <div className="secondary uk-margin-small-top">{searchError}</div>
          )}
          {hasSearched && !searching && !searchError && !auditReservations.length && (
            <div className="secondary uk-margin-small-top">
              No matching reservations found for this check-in date and guest last name.
            </div>
          )}
        </div>

        <div className="uk-overflow-auto">
          <table className="uk-table uk-table-hover uk-table-divider hms-table uk-margin-small-top">
            <thead>
              <tr>
                <th>SCHEDULED MESSAGE</th>
                <th>SCHEDULED AT</th>
                <th>SENT AT</th>
                <th>SENT</th>
                <th>UNSENT REASON</th>
              </tr>
            </thead>
            <tbody>
              {messageAudit?.length ? (
                messageAudit.map((msg, idx) => (
                  <tr key={msg.scheduled_message_receipt_id || `${msg.scheduled_message_id}-${msg.guest_reservation_id}-${idx}`}>
                    <td>{msg.scheduled_message?.name || "-"}</td>
                    <td>{formatDateTime(msg.scheduled_at || msg.sent_at)}</td>
                    <td>{formatDateTime(msg.event_created_at || msg.created_at)}</td>
                    <td>{msg.sent ? "Yes" : "No"}</td>
                    <td className="message">
                      <textarea rows="1" disabled>
                        {msg.error || ""}
                      </textarea>
                      <div></div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="no-results">
                    No message audit found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </li>
  );
};

export default MessageAudit;